export const userFileSystemReloadAttemptKey = (userId: string): string =>
  `userFileSystemReloadAttempted:${userId}`;

export const USER_FILE_SYSTEM_RELOAD_ATTEMPT_TTL_MS = 24 * 60 * 60 * 1000;

type StoredAttempt = { timestamp: number };

const isValidStoredAttempt = (value: unknown): value is StoredAttempt =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as StoredAttempt).timestamp === 'number' &&
  Number.isFinite((value as StoredAttempt).timestamp);

export const hasAttemptedUserFileSystemReload = (userId: string): boolean => {
  const key = userFileSystemReloadAttemptKey(userId);
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) {
      return false;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      window.localStorage.removeItem(key);
      return false;
    }

    if (!isValidStoredAttempt(parsed)) {
      window.localStorage.removeItem(key);
      return false;
    }

    const age = Date.now() - parsed.timestamp;
    if (age < 0 || age >= USER_FILE_SYSTEM_RELOAD_ATTEMPT_TTL_MS) {
      window.localStorage.removeItem(key);
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

export const markUserFileSystemReloadAttempted = (userId: string): void => {
  try {
    const value: StoredAttempt = { timestamp: Date.now() };
    window.localStorage.setItem(
      userFileSystemReloadAttemptKey(userId),
      JSON.stringify(value),
    );
  } catch {
    // localStorage may be unavailable (e.g. sandboxed contexts); fail open so
    // the reload itself still happens.
  }
};

export const clearUserFileSystemReloadAttempt = (userId: string): void => {
  try {
    window.localStorage.removeItem(userFileSystemReloadAttemptKey(userId));
  } catch {
    // see markUserFileSystemReloadAttempted
  }
};
